import type { UserProfile, UserPreferences, TradingExperience, Instrument } from '../../types/user'
import { getAPIUrl } from '../../config/api.config'
import { logger } from '../../utils/logger'
import { getAuthTokenAsync, notifySessionExpired, notifyAccountSuspended, notifyRegistrationClosed } from './authTokenManager'

async function getAuthHeadersAsync(): Promise<Record<string, string>> {
  const token = await getAuthTokenAsync()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

// Lazy initialization to avoid circular dependency issues
let API_BASE_URL: string | null = null

const getApiBaseUrl = (): string => {
  if (!API_BASE_URL) {
    API_BASE_URL = getAPIUrl()
  }
  return API_BASE_URL
}

interface ApiResponse<T> {
  success: boolean
  data: T
  error?: string
}

interface OnboardingData {
  experience: TradingExperience
  selectedInstruments: Instrument[]
  emailConsent?: boolean
}

export class UserService {
  private static checkSessionExpired(response: Response): void {
    if (response.status === 401) {
      notifySessionExpired();
      return;
    }
    if (response.status === 403) {
      // Fire-and-forget parse body, detect ACCOUNT_SUSPENDED
      response.clone().json().then((body: unknown) => {
        const code = (body as { errorCode?: string; code?: string })?.errorCode ?? (body as { code?: string })?.code;
        if (code === 'ACCOUNT_SUSPENDED') {
          notifyAccountSuspended();
        } else if (code === 'REGISTRATION_CLOSED') {
          notifyRegistrationClosed();
        }
      }).catch(() => { /* non-JSON body */ });
    }
  }

  // Get or create user profile
  static async getUserProfile(userId: string): Promise<UserProfile> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/${userId}/profile`, {
        method: 'GET',
        headers: await getAuthHeadersAsync(),
      })

      UserService.checkSessionExpired(response)

      // Handle server errors more gracefully
      if (response.status === 500) {
        logger.warn('Server error fetching profile, will retry with create')
        throw new Error('Server error - will create new profile')
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json() as ApiResponse<UserProfile>

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to get user profile')
      }

      return result.data
    } catch (error) {
      logger.error('Error getting user profile:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to get user profile')
    }
  }

  // Update user profile
  static async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const url = `${getApiBaseUrl()}/api/users/${userId}/profile`;
      if (import.meta.env.DEV) {
        logger.info('UserService: Updating profile', { url, updates });
      }
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: await getAuthHeadersAsync(),
        body: JSON.stringify(updates)
      })

      UserService.checkSessionExpired(response)

      if (import.meta.env.DEV) {
        logger.info('UserService: Response status', { status: response.status, statusText: response.statusText });
      }

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('UserService: HTTP error response', { errorText });
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
      }

      const result = await response.json() as ApiResponse<UserProfile>
      if (import.meta.env.DEV) {
        logger.info('UserService: API response', { result });
      }

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to update user profile')
      }

      return result.data
    } catch (error) {
      logger.error('UserService: Error updating user profile:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to update user profile')
    }
  }

  // Complete onboarding
  static async completeOnboarding(userId: string, onboardingData: OnboardingData): Promise<{
    profile: UserProfile
    preferences: UserPreferences
  }> {
    try {
      const url = `${getApiBaseUrl()}/api/users/${userId}/onboarding/complete`;
      if (import.meta.env.DEV) {
        logger.info('UserService: Completing onboarding', { url, onboardingData });
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: await getAuthHeadersAsync(),
        body: JSON.stringify(onboardingData)
      })

      UserService.checkSessionExpired(response)

      if (import.meta.env.DEV) {
        logger.info('UserService: Onboarding response status', { status: response.status, statusText: response.statusText });
      }

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('UserService: Onboarding HTTP error response', { errorText });
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
      }

      const result = await response.json() as ApiResponse<{
        message: string
        profile: UserProfile
        preferences: UserPreferences
      }>
      
      if (import.meta.env.DEV) {
        logger.info('UserService: Onboarding API response', { result });
      }

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to complete onboarding')
      }

      return {
        profile: result.data.profile,
        preferences: result.data.preferences
      }
    } catch (error) {
      logger.error('UserService: Error completing onboarding:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to complete onboarding')
    }
  }

  // Skip onboarding
  static async skipOnboarding(userId: string): Promise<{
    profile: UserProfile
    preferences: UserPreferences
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/${userId}/onboarding/skip`, {
        method: 'POST',
        headers: await getAuthHeadersAsync(),
      })

      UserService.checkSessionExpired(response)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json() as ApiResponse<{
        message: string
        profile: UserProfile
        preferences: UserPreferences
      }>

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to skip onboarding')
      }

      return {
        profile: result.data.profile,
        preferences: result.data.preferences
      }
    } catch (error) {
      logger.error('Error skipping onboarding:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to skip onboarding')
    }
  }

  // Get user preferences
  static async getUserPreferences(userId: string): Promise<UserPreferences> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/${userId}/preferences`, {
        method: 'GET',
        headers: await getAuthHeadersAsync(),
      })

      UserService.checkSessionExpired(response)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json() as ApiResponse<UserPreferences>

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to get user preferences')
      }

      return result.data
    } catch (error) {
      logger.error('Error getting user preferences:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to get user preferences')
    }
  }

  // Get or create user (used on login)
  static async getOrCreateUser(userId: string, authData?: {
    email?: string
    displayName?: string
    photoURL?: string
  }): Promise<UserProfile> {
    try {
      // First try to get existing profile
      let profile = await this.getUserProfile(userId)

      // If profile exists but we have updated auth data, update it
      if (profile && authData) {
        const updates = {
          ...authData,
          lastLoginAt: new Date().toISOString()
        }
        profile = await this.updateUserProfile(userId, updates)
      }

      return profile
    } catch (error) {
      // If profile doesn't exist or server error, create it
      if (error instanceof Error && 
          (error.message.includes('404') || 
           error.message.includes('500') || 
           error.message.includes('Server error'))) {
        if (import.meta.env.DEV) {
          logger.info('Creating new profile for user:', { userId })
        }
        const newProfile = {
          uid: userId,
          email: authData?.email ?? '',
          ...(authData?.displayName && { displayName: authData.displayName }),
          ...(authData?.photoURL && { photoURL: authData.photoURL }),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
          onboardingComplete: false
        }

        return await this.updateUserProfile(userId, newProfile)
      }

      throw error
    }
  }

  // Get user performance statistics
  static async getUserStats(userId: string): Promise<{
    totalSignalsFollowed: number
    winRate: number
    totalProfitPips: number
    winningSignals: number
    losingSignals: number
    activeSignals: number
    averageRiskReward: number
    monthlyProfit: number
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/${userId}/stats`, {
        method: 'GET',
        headers: await getAuthHeadersAsync(),
      })

      UserService.checkSessionExpired(response)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json() as ApiResponse<{
        totalSignalsFollowed: number
        winRate: number
        totalProfitPips: number
        winningSignals: number
        losingSignals: number
        activeSignals: number
        averageRiskReward: number
        monthlyProfit: number
      }>

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to get user statistics')
      }

      return result.data
    } catch (error) {
      logger.error('Error getting user stats:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to get user statistics')
    }
  }

  // Get detailed user performance
  static async getUserPerformance(userId: string, period: '1d' | '7d' | '30d' | '90d' = '30d'): Promise<{
    dailyStats: {
      date: string
      totalProfit: number
      totalSignals: number
      winRate: number
    }[]
    monthlyStats: {
      month: string
      totalProfit: number
      totalSignals: number
      winRate: number
    }[]
    instrumentPerformance: {
      instrument: string
      totalSignals: number
      winRate: number
      avgProfit: number
    }[]
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/${userId}/performance?period=${period}`, {
        method: 'GET',
        headers: await getAuthHeadersAsync(),
      })

      UserService.checkSessionExpired(response)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json() as ApiResponse<{
        dailyStats: {
          date: string
          totalProfit: number
          totalSignals: number
          winRate: number
        }[]
        monthlyStats: {
          month: string
          totalProfit: number
          totalSignals: number
          winRate: number
        }[]
        instrumentPerformance: {
          instrument: string
          totalSignals: number
          winRate: number
          avgProfit: number
        }[]
      }>

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to get user performance')
      }

      return result.data
    } catch (error) {
      logger.error('Error getting user performance:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to get user performance')
    }
  }

  // Follow a signal for tracking
  static async followSignal(userId: string, signalId: string): Promise<{
    success: boolean
    trackingId: string
    message: string
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/${userId}/signals/follow`, {
        method: 'POST',
        headers: await getAuthHeadersAsync(),
        body: JSON.stringify({ signalId })
      })

      UserService.checkSessionExpired(response)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json() as ApiResponse<{
        trackingId: string
        message: string
      }>

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to follow signal')
      }

      return {
        success: true,
        trackingId: result.data.trackingId,
        message: result.data.message
      }
    } catch (error) {
      logger.error('Error following signal:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to follow signal')
    }
  }

  // Record signal result
  static async recordSignalResult(userId: string, signalId: string, result: 'win' | 'loss' | 'breakeven', profitPips?: number): Promise<{
    success: boolean
    message: string
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/${userId}/signals/result`, {
        method: 'POST',
        headers: await getAuthHeadersAsync(),
        body: JSON.stringify({
          signalId,
          result,
          ...(profitPips !== undefined && { profitPips })
        })
      })

      UserService.checkSessionExpired(response)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result_data = await response.json() as ApiResponse<{
        message: string
      }>

      if (!result_data.success) {
        throw new Error(result_data.error ?? 'Failed to record signal result')
      }

      return {
        success: true,
        message: result_data.data.message
      }
    } catch (error) {
      logger.error('Error recording signal result:', { error: error instanceof Error ? error.message : String(error) })
      throw new Error(error instanceof Error ? error.message : 'Failed to record signal result')
    }
  }
}